import { useEffect, useState } from "react";
import { Download, Eye, FileText, FileImage, FileAudio, FileVideo, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fileError } from "../../hooks/useAttachments";
import { AttachmentPreview } from "./AttachmentPreview";

export const fileSize = (size) => size < 1024 * 1024 ? `${Math.max(1, Math.round(size / 1024))} KB` : `${(size / 1024 / 1024).toFixed(1)} MB`;
export const fileStatus = (file) => file.is_deleted ? "Deleted" : ({ readable: "Ready to read", partial: "Partial text", stored_only: "Stored · not analysed" }[file.analysis_status]);
export const FileIcon = ({ type, size = 18 }) => {
  const Icon = type?.startsWith("image/") ? FileImage : type?.startsWith("audio/") ? FileAudio : type?.startsWith("video/") ? FileVideo : FileText;
  return <Icon size={size} className="shrink-0" />;
};

export async function downloadAttachment(file) {
  const { data } = await api.get(`/files/${file.file_id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const link = document.createElement("a");
  link.href = url; link.download = file.original_filename; link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function AttachmentList({ files = [], prefix, onRemove, disabled = false }) {
  const [deleted, setDeleted] = useState([]);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    const listener = (event) => setDeleted(prev => [...prev, event.detail]);
    window.addEventListener("aria-file-deleted", listener);
    return () => window.removeEventListener("aria-file-deleted", listener);
  }, []);
  if (!files.length) return null;
  return <div className="space-y-1.5 min-w-0 mt-2" data-testid={`${prefix}-attachments`}>
    {files.map(original => {
      const file = deleted.includes(original.file_id) ? { ...original, is_deleted: true } : original;
      const id = `${prefix}-file-${file.file_id}`;
      return <div key={file.file_id} data-testid={id} className="flex min-w-0 items-center gap-2 rounded-md border border-current/20 p-2 text-xs">
        <FileIcon type={file.content_type} />
        <div className="min-w-0 flex-1">
          <div title={file.original_filename} className="truncate font-medium" data-testid={`${id}-name`}>{file.original_filename}</div>
          <div className="opacity-70 text-[10px] mt-0.5" data-testid={`${id}-status`}>{fileSize(file.size)} · {fileStatus(file)}</div>
        </div>
        {!file.is_deleted && <>
          <button type="button" title={`Preview ${file.original_filename}`} aria-label={`Preview ${file.original_filename}`} data-testid={`${id}-preview`}
            onClick={() => setPreview(file)} className="shrink-0 p-1.5 rounded hover:bg-current/10 transition-colors"><Eye size={14} /></button>
          <button type="button" title={`Download ${file.original_filename}`} aria-label={`Download ${file.original_filename}`} data-testid={`${id}-download`}
            disabled={busy === file.file_id} onClick={async () => { setBusy(file.file_id); try { await downloadAttachment(file); } catch (e) { toast.error(fileError(e)); } finally { setBusy(null); } }}
            className="shrink-0 p-1.5 rounded hover:bg-current/10 transition-colors">
            {busy === file.file_id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          </button>
        </>}
        {onRemove && <button type="button" title="Remove attachment from message" aria-label={`Remove ${file.original_filename}`} disabled={disabled}
          onClick={() => onRemove(file.file_id)} data-testid={`${id}-remove`} className="p-1.5 shrink-0 rounded hover:bg-current/10"><X size={14} /></button>}
      </div>;
    })}
    <AttachmentPreview file={preview} onClose={() => setPreview(null)} prefix={prefix} />
  </div>;
}